import React, { useState, useEffect } from 'react';
import ConversationItem from './ConversationItem';
import { Megaphone } from 'lucide-react';
import BroadcastModal from './BroadcastModal';
import { searchLeads, getOrCreateConversation } from '../../firebase/conversation.service';
import { useAuth } from '../../context/AuthContext';

const ConversationList = ({ conversations, activeChat, openChat, filter = 'all', setFilter }) => {
    const { user } = useAuth();
    const [isBroadcastModalOpen, setBroadcastModalOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleOpenBroadcastModal = () => {
        setBroadcastModalOpen(true);
    };

    const handleCloseBroadcastModal = () => {
        setBroadcastModalOpen(false);
    };

    // Handle search
    const handleSearch = async (value) => {
        setSearchInput(value);

        if (!value.trim()) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        if (!user?.uid) {
            setError('User not authenticated');
            return;
        }

        setIsSearching(true);
        setLoading(true);
        setError(null);

        try {
            const results = await searchLeads(user.uid, value);
            setSearchResults(results);
        } catch (err) {
            console.error('Search error:', err);
            setError('Failed to search leads');
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle selecting a lead from search results
    const handleSelectLead = async (lead) => {
        if (!user?.uid) {
            setError('User not authenticated');
            return;
        }

        try {
            setLoading(true);
            // Create or get existing conversation
            const conversation = await getOrCreateConversation(user.uid, lead);
            // Open the chat using the conversation ID
            openChat(conversation.id);
            // Clear search
            setSearchInput('');
            setSearchResults([]);
            setIsSearching(false);
        } catch (err) {
            console.error('Error opening conversation:', err);
            setError('Failed to open conversation');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex-1 overflow-y-auto">
            {/* Header Section */}
            <div className="flex items-center justify-between p-4 border-b border-notion-border">
                <h2 className="text-lg font-semibold text-notion-text">Inbox</h2>
                <button
                    className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-white bg-stone-800 rounded hover:bg-stone-700 transition"
                    onClick={handleOpenBroadcastModal}
                >
                    <Megaphone className="w-4 h-4" /> Broadcast
                </button>
            </div>

            {/* Search and Filters */}
            <div className="p-4 border-b border-notion-border">
                <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchInput}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-notion-border rounded focus:outline-none focus:ring-1 focus:ring-stone-800"
                />
                {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
                <div className="flex gap-2 mt-2">
                    <button
                        onClick={() => setFilter?.('all')}
                        className={`px-3 py-1 text-xs font-medium rounded transition ${filter === 'all' ? 'text-white bg-stone-800 hover:bg-stone-700' : 'text-stone-800 bg-stone-100 hover:bg-stone-200'}`}
                    >All</button>
                    <button
                        onClick={() => setFilter?.('unread')}
                        className={`px-3 py-1 text-xs font-medium rounded transition ${filter === 'unread' ? 'text-white bg-stone-800 hover:bg-stone-700' : 'text-stone-800 bg-stone-100 hover:bg-stone-200'}`}
                    >Unread</button>
                </div>
            </div>

            {/* Search Results */}
            {isSearching && searchResults.length > 0 && (
                <div className="border-b border-notion-border max-h-64 overflow-y-auto bg-stone-50">
                    <p className="px-4 py-2 text-xs font-medium text-stone-500">SEARCH RESULTS</p>
                    {searchResults.map((lead) => (
                        <button
                            key={lead.id}
                            onClick={() => handleSelectLead(lead)}
                            disabled={loading}
                            className="w-full text-left px-4 py-3 border-b border-notion-border hover:bg-stone-100 transition disabled:opacity-50"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-sm text-notion-text">{lead.fullName || lead.name}</h3>
                                    <p className="text-xs text-stone-500 truncate">{lead.email || lead.phone}</p>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {isSearching && loading && (
                <div className="px-4 py-6 text-center">
                    <p className="text-sm text-stone-500">Searching...</p>
                </div>
            )}

            {isSearching && searchResults.length === 0 && !loading && searchInput && (
                <div className="px-4 py-6 text-center">
                    <p className="text-sm text-stone-500">No leads found</p>
                </div>
            )}

            {/* Conversation List */}
            {!isSearching && (
                <div className="flex-1 overflow-y-auto">
                    {(() => {
                        const filtered = filter === 'unread'
                            ? conversations.filter(c => (c.unreadCount > 0) || (c.unread === true))
                            : conversations;
                        if (filtered.length === 0) {
                            return (
                                <div className="p-4 text-center">
                                    <p className="text-sm text-stone-500">
                                        {filter === 'unread' ? 'No unread conversations' : 'No active conversations. Search for leads to start chatting!'}
                                    </p>
                                </div>
                            );
                        }
                        return filtered.map((convo) => (
                            <ConversationItem
                                key={convo.id}
                                conversation={convo}
                                isActive={activeChat === convo.id}
                                onClick={() => openChat(convo.id)}
                            />
                        ));
                    })()}
                </div>
            )}

            {/* Broadcast Modal */}
            {isBroadcastModalOpen && <BroadcastModal onClose={handleCloseBroadcastModal} />}
        </div>
    );
};

export default ConversationList;